import { SYSTEM_FIELD_TYPES } from '@servicetim/domain';
import type {
  FieldConfig,
  FieldDefinition,
  FieldOption,
  FieldType,
  Status,
  SystemField,
  WorkflowDetail,
} from '@servicetim/shared';
import {
  adminPath,
  useApiContext,
  useFieldBindings,
  useIssueTypes,
  usePriorities,
  useProject,
  useProjectFields,
  useWorkflows,
} from '@servicetim/ui/api';
import type { SchemaFormOption } from '@servicetim/ui/components';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

/** Filter semantics of a field: a registry type, or full-text search for the virtual `text`. */
export type CatalogFieldType = FieldType | 'text_search';

export interface CatalogField {
  /** `status_id`, `cf_xxxxxxxx`; what the AST and rule JSON reference. */
  key: string;
  label: string;
  type: CatalogFieldType;
  system: boolean;
  scope: 'system' | 'project' | 'tenant';
  /** Active options for select-like fields (statuses, priorities, issue types, custom selects). */
  options?: SchemaFormOption[];
  fieldId?: string;
  archived: boolean;
  indexed: boolean;
  config?: FieldConfig;
}

export const SYSTEM_FIELD_LABELS: Record<SystemField, string> = {
  id: 'Ticket id',
  key: 'Ticket key',
  number: 'Ticket number',
  project_id: 'Project',
  issue_type_id: 'Issue type',
  status_id: 'Status',
  'status.category': 'Status category',
  priority_id: 'Priority',
  'priority.rank': 'Priority rank',
  summary: 'Summary',
  description: 'Description',
  reporter_id: 'Reporter',
  assignee_id: 'Assignee',
  created_by: 'Created by',
  organization_id: 'Organization',
  labels: 'Labels',
  watchers: 'Watchers',
  parent_id: 'Parent ticket',
  created_at: 'Created',
  updated_at: 'Updated',
  resolved_at: 'Resolved',
  due_at: 'Due',
  acknowledged_at: 'Acknowledged',
  acknowledged_by: 'Acknowledged by',
  text: 'Text search',
};

/** Order the pickers list system fields in (most used first). */
export const SYSTEM_FIELD_ORDER: readonly SystemField[] = [
  'status_id',
  'status.category',
  'priority_id',
  'issue_type_id',
  'assignee_id',
  'reporter_id',
  'summary',
  'description',
  'text',
  'labels',
  'watchers',
  'organization_id',
  'parent_id',
  'created_at',
  'updated_at',
  'resolved_at',
  'due_at',
  'acknowledged_at',
  'acknowledged_by',
  'created_by',
  'project_id',
  'priority.rank',
  'number',
  'key',
  'id',
];

export const STATUS_CATEGORY_OPTIONS: SchemaFormOption[] = [
  { id: 'todo', label: 'To do' },
  { id: 'in_progress', label: 'In progress' },
  { id: 'done', label: 'Done' },
];

/** System fields a post-function may set (the rest are derived or immutable). */
export const SETTABLE_SYSTEM_FIELDS: ReadonlySet<string> = new Set<SystemField>([
  'summary',
  'description',
  'priority_id',
  'assignee_id',
  'labels',
  'due_at',
  'resolved_at',
  'organization_id',
]);

export const isSelectType = (type: CatalogFieldType): boolean =>
  type === 'single_select' || type === 'multi_select';

export const isUserType = (type: CatalogFieldType): boolean =>
  type === 'user' || type === 'multi_user';

export interface FieldCatalog {
  fields: CatalogField[];
  system: CatalogField[];
  custom: CatalogField[];
  byKey: ReadonlyMap<string, CatalogField>;
  /** Raw definitions visible to the project (for editors). */
  definitions: FieldDefinition[];
  /** Options per field id, archived included (editors); the catalog itself exposes active ones. */
  optionsByFieldId: Record<string, FieldOption[]>;
  /** Every status of every workflow of the project (draft). */
  statuses: (Status & { workflowId: string; workflowName: string })[];
  isPending: boolean;
  error: unknown;
}

const SELECT_TYPES = new Set<FieldType>(['single_select', 'multi_select']);

/**
 * Everything the query builder, rule builder and form builder need to offer fields, operators
 * and value controls for one project: system fields with their option lists (statuses,
 * priorities, issue types) and the project's bound custom fields with their definitions and
 * active options. Labels come from the API rows already loaded, never from local maps of ids.
 */
export function useFieldCatalog(projectId: string | undefined): FieldCatalog {
  const { request, keys } = useApiContext();
  const project = useProject(projectId);
  const bindings = useFieldBindings(projectId);
  const definitions = useProjectFields(projectId);
  const issueTypes = useIssueTypes(projectId);
  const priorities = usePriorities(projectId);
  const workflows = useWorkflows(projectId);

  const workflowDetails = useQueries({
    queries: (workflows.data ?? []).map((w) => ({
      queryKey: keys.project(projectId ?? '').workflow(w.id),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        request<WorkflowDetail>('GET', adminPath(`/projects/${projectId}/workflows/${w.id}`), {
          signal,
        }),
      enabled: projectId !== undefined,
      staleTime: 30_000,
    })),
  });

  const selectFieldIds = useMemo(
    () => (bindings.data ?? []).filter((b) => SELECT_TYPES.has(b.fieldType)).map((b) => b.fieldId),
    [bindings.data],
  );
  const optionQueries = useQueries({
    queries: selectFieldIds.map((fieldId) => ({
      queryKey: keys.fields.options(fieldId),
      queryFn: ({ signal }: { signal: AbortSignal }) =>
        request<FieldOption[]>('GET', adminPath(`/fields/${fieldId}/options`), { signal }),
      staleTime: 30_000,
    })),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: derived from the query results only
  const optionsByFieldId = useMemo(() => {
    const out: Record<string, FieldOption[]> = {};
    selectFieldIds.forEach((fieldId, i) => {
      const data = optionQueries[i]?.data;
      if (data) out[fieldId] = data;
    });
    return out;
  }, [selectFieldIds, ...optionQueries.map((q) => q.data)]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: derived from the query results only
  const statuses = useMemo(() => {
    const out: FieldCatalog['statuses'] = [];
    (workflows.data ?? []).forEach((w, i) => {
      const detail = workflowDetails[i]?.data;
      if (!detail) return;
      for (const s of detail.draft.statuses) {
        out.push({ ...s, workflowId: w.id, workflowName: w.name });
      }
    });
    return out;
  }, [workflows.data, ...workflowDetails.map((q) => q.data)]);

  const catalog = useMemo(() => {
    const manyWorkflows = (workflows.data?.length ?? 0) > 1;
    const systemOptions: Partial<Record<SystemField, SchemaFormOption[]>> = {
      status_id: statuses.map((s) => ({
        id: s.id,
        label: manyWorkflows ? `${s.name} (${s.workflowName})` : s.name,
        color: s.color,
      })),
      'status.category': STATUS_CATEGORY_OPTIONS,
      priority_id: (priorities.data ?? []).map((p) => ({
        id: p.id,
        label: p.name,
        color: p.color,
      })),
      issue_type_id: (issueTypes.data ?? []).map((t) => ({ id: t.id, label: t.name })),
      project_id: project.data
        ? [{ id: project.data.id, label: `${project.data.key} · ${project.data.name}` }]
        : [],
    };
    const system: CatalogField[] = SYSTEM_FIELD_ORDER.map((key) => ({
      key,
      label: SYSTEM_FIELD_LABELS[key],
      type: SYSTEM_FIELD_TYPES[key],
      system: true,
      scope: 'system',
      options: systemOptions[key],
      archived: false,
      indexed: true,
    }));
    const byId = new Map((definitions.items ?? []).map((d) => [d.id, d]));
    const custom: CatalogField[] = [...(bindings.data ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((b) => {
        const def = byId.get(b.fieldId);
        return {
          key: b.fieldKey,
          label: b.fieldLabel,
          type: b.fieldType,
          system: false,
          scope: b.fieldScope,
          fieldId: b.fieldId,
          archived: def?.archivedAt != null,
          indexed: def?.indexed ?? false,
          config: def?.config,
          options: optionsByFieldId[b.fieldId]
            ?.filter((o) => o.archivedAt === null)
            .map((o) => ({ id: o.id, label: o.label, color: o.color })),
        };
      });
    const fields = [...system, ...custom];
    return { fields, system, custom, byKey: new Map(fields.map((f) => [f.key, f])) };
  }, [
    workflows.data,
    statuses,
    priorities.data,
    issueTypes.data,
    project.data,
    definitions.items,
    bindings.data,
    optionsByFieldId,
  ]);

  const error =
    bindings.error ?? definitions.error ?? issueTypes.error ?? priorities.error ?? workflows.error;

  return {
    ...catalog,
    definitions: definitions.items,
    optionsByFieldId,
    statuses,
    isPending: bindings.isPending || definitions.isPending,
    error: error ?? null,
  };
}
